import { FC, useCallback, useContext, useEffect, useState } from "react";

import { History, OpenInNew, Search } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControlLabel,
  InputAdornment,
  Paper,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import {
  ConnectCallHistoryRow,
  listConnectMeeting,
} from "@rubi/api-generated/clientRs";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { AuthStateContext } from "@/contexts/AuthContext";

import LifecycleChip from "./LifecycleChip";
import MeetingHistoryDialog from "./MeetingHistoryDialog";

type Phase = "all" | "upcoming" | "live" | "ended" | "archived";

const PHASES: { value: Phase; labelKey: string }[] = [
  { value: "all", labelKey: "callHistory.phases.all" },
  { value: "upcoming", labelKey: "callHistory.phases.upcoming" },
  { value: "live", labelKey: "callHistory.phases.live" },
  { value: "ended", labelKey: "callHistory.phases.ended" },
  { value: "archived", labelKey: "callHistory.phases.archived" },
];

const EXPORT_LABEL_KEYS: Record<string, string> = {
  pending: "callHistory.exportStatus.pending",
  in_progress: "callHistory.exportStatus.inProgress",
  successful: "callHistory.exportStatus.successful",
  failed: "callHistory.exportStatus.failed",
  re_export_requested: "callHistory.exportStatus.reExportRequested",
};

const fmtWhen = (m: ConnectCallHistoryRow) => {
  const iso = m.scheduled_at ?? m.time_created;
  const opts: Intl.DateTimeFormatOptions = {
    dateStyle: "medium",
    timeStyle: "short",
  };
  if (m.timezone) opts.timeZone = m.timezone;
  try {
    return new Date(iso).toLocaleString(undefined, opts);
  } catch {
    return new Date(iso).toLocaleString();
  }
};

const fmtDuration = (t: TFunction, m: ConnectCallHistoryRow) => {
  if (!m.started_at || !m.ended_at) return "";
  const secs = Math.max(
    0,
    Math.round(
      (new Date(m.ended_at).getTime() - new Date(m.started_at).getTime()) /
        1000,
    ),
  );
  const h = Math.floor(secs / 3600);
  const mm = Math.floor((secs % 3600) / 60);
  return h
    ? t("callHistory.duration.hoursMinutes", { hours: h, minutes: mm })
    : t("callHistory.duration.minutes", { minutes: mm });
};

const CallHistoryPage: FC = () => {
  const { t } = useTranslation("connect");
  const navigate = useNavigate();
  const { currentUser, getOrgIDFromURL } = useContext(AuthStateContext);
  const orgId = getOrgIDFromURL() ?? currentUser?.rubi_org_id ?? "";

  const [rows, setRows] = useState<ConnectCallHistoryRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [phase, setPhase] = useState<Phase>("all");
  const [search, setSearch] = useState("");
  const [mine, setMine] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [historyFor, setHistoryFor] = useState<ConnectCallHistoryRow | null>(
    null,
  );

  const load = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const res = await listConnectMeeting({
        path: { org_id: orgId },
        query: {
          phase: phase === "all" ? undefined : phase,
          search: search.trim() || undefined,
          mine: mine || undefined,
          limit: pageSize,
          page,
        },
      });
      setRows(res.data?.meetings ?? []);
      setTotal(res.data?.total ?? 0);
      setFailed(false);
    } catch {
      // Stale rows under a new filter, and the empty state, both read as
      // "no calls" when the truth is that we don't know.
      setRows([]);
      setTotal(0);
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [orgId, phase, search, mine, page, pageSize]);

  useEffect(() => {
    const id = setTimeout(() => void load(), search ? 300 : 0);
    return () => clearTimeout(id);
  }, [load, search]);

  const columns: GridColDef<ConnectCallHistoryRow>[] = [
    {
      field: "scheduled_at",
      headerName: t("callHistory.columns.when"),
      minWidth: 170,
      flex: 0.9,
      valueGetter: (_v, row) => row.scheduled_at ?? row.time_created,
      renderCell: (p) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          {fmtWhen(p.row)}
        </Box>
      ),
    },
    {
      field: "occurrence_number",
      headerName: t("callHistory.columns.occurrence"),
      minWidth: 150,
      flex: 0.8,
      renderCell: (p) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            height: "100%",
          }}
        >
          <Chip
            size="small"
            variant="outlined"
            color="primary"
            label={p.row.occurrence_number || "—"}
            sx={{ fontFamily: "monospace", fontWeight: 600 }}
          />
          {p.row.reference_tag && (
            <Typography variant="caption" color="text.secondary" noWrap>
              {p.row.reference_tag}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: "title",
      headerName: t("callHistory.columns.meeting"),
      minWidth: 200,
      flex: 1.4,
      renderCell: (p) => (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            height: "100%",
            minWidth: 0,
          }}
        >
          <Typography variant="body2" noWrap>
            {p.row.title}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {p.row.meeting_number ?? ""}
            {p.row.lifecycle.invitation_count
              ? ` · ${t("callHistory.invited", {
                  count: p.row.lifecycle.invitation_count,
                })}`
              : ""}
          </Typography>
        </Box>
      ),
    },
    {
      field: "lifecycle",
      headerName: t("callHistory.columns.status"),
      minWidth: 150,
      flex: 0.8,
      renderCell: (p) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <LifecycleChip lifecycle={p.row.lifecycle} showPurge={false} />
        </Box>
      ),
    },
    {
      field: "duration",
      headerName: t("callHistory.columns.length"),
      minWidth: 90,
      flex: 0.5,
      renderCell: (p) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          {fmtDuration(t, p.row)}
        </Box>
      ),
    },
    {
      field: "export",
      headerName: t("callHistory.columns.evidence"),
      minWidth: 170,
      flex: 0.9,
      renderCell: (p) => {
        const l = p.row.lifecycle;
        if (!l.export_status) {
          return l.recording_status === "error" ? (
            <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
              <Typography variant="body2" color="text.secondary" noWrap>
                {t("callHistory.recordingFailed")}
              </Typography>
            </Box>
          ) : null;
        }
        const label = EXPORT_LABEL_KEYS[l.export_status]
          ? t(EXPORT_LABEL_KEYS[l.export_status])
          : l.export_status;
        const purge = l.purge_after
          ? t("callHistory.purgeOn", {
              date: new Date(l.purge_after).toLocaleDateString(),
            })
          : l.recording_status === "purged"
            ? t("callHistory.purged")
            : "";
        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              height: "100%",
              minWidth: 0,
            }}
          >
            <Typography variant="body2" noWrap>
              {label}
            </Typography>
            {purge && (
              <Typography variant="caption" color="text.secondary" noWrap>
                {purge}
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      field: "actions",
      headerName: "",
      width: 110,
      renderCell: (p) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            height: "100%",
          }}
        >
          <Tooltip title={t("callHistory.actions.historyTooltip")}>
            <Button
              size="small"
              startIcon={<History />}
              onClick={(e) => {
                e.stopPropagation();
                setHistoryFor(p.row);
              }}
            >
              {t("callHistory.actions.history")}
            </Button>
          </Tooltip>
          {p.row.occurrence_id && (
            <Tooltip title={t("callHistory.actions.openOccurrence")}>
              <Button
                size="small"
                sx={{ minWidth: 0, px: 0.5 }}
                aria-label={t("callHistory.actions.openOccurrence")}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(
                    `/rubiconnect/${orgId}/occurrences/${p.row.occurrence_id}`,
                  );
                }}
              >
                <OpenInNew fontSize="small" />
              </Button>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 0.5, fontWeight: 600 }}>
        {t("callHistory.title")}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t("callHistory.description")}
      </Typography>

      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        alignItems={{ md: "center" }}
        sx={{ mb: 2 }}
      >
        <Tabs
          value={phase}
          onChange={(_, v: Phase) => {
            setPhase(v);
            setPage(0);
          }}
          variant="scrollable"
          allowScrollButtonsMobile
          sx={{ minHeight: 40, "& .MuiTab-root": { minHeight: 40 } }}
        >
          {PHASES.map((p) => (
            <Tab key={p.value} value={p.value} label={t(p.labelKey)} />
          ))}
        </Tabs>
        <Box sx={{ flex: 1 }} />
        <TextField
          size="small"
          placeholder={t("callHistory.searchPlaceholder")}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 260 }}
        />
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={mine}
              onChange={(e) => {
                setMine(e.target.checked);
                setPage(0);
              }}
            />
          }
          label={t("callHistory.mySessions")}
        />
      </Stack>

      {failed ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => void load()}>
              {t("callHistory.retry")}
            </Button>
          }
        >
          {t("callHistory.loadError")}
        </Alert>
      ) : (
        <Paper elevation={2}>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            autoHeight
            rowHeight={60}
            disableColumnMenu
            // Server-paged against an endpoint with no sort contract, so the
            // grid's own sorting would only reorder the page in view.
            disableColumnSorting
            paginationMode="server"
            rowCount={total}
            paginationModel={{ page, pageSize }}
            onPaginationModelChange={(m) => {
              setPage(m.page);
              setPageSize(m.pageSize);
            }}
            pageSizeOptions={[10, 25, 50, 100]}
            onRowClick={(p) => setHistoryFor(p.row as ConnectCallHistoryRow)}
            sx={{
              border: "none",
              "& .MuiDataGrid-row": { cursor: "pointer" },
              "& .MuiDataGrid-cell": {
                borderBottom: "1px solid rgba(224, 224, 224, 0.5)",
              },
            }}
            localeText={{
              noRowsLabel:
                phase === "all" && !search && !mine
                  ? t("callHistory.empty")
                  : t("callHistory.noMatches"),
            }}
          />
        </Paper>
      )}

      {historyFor && (
        <MeetingHistoryDialog
          open
          onClose={() => setHistoryFor(null)}
          orgId={orgId}
          meeting={historyFor}
        />
      )}
    </Box>
  );
};

export default CallHistoryPage;
